import React, { memo } from 'react';
import { useTheme, getTokens } from 'tamagui';
import AddCommentIcon from '@mui/icons-material/AddComment';
import { CobuText, CobuView, CobuStack } from '@cobu/ui';
import { getFloatingBottomOffset } from '../../constants/layout';

interface FloatingCommentButtonProps {
  onPress: () => void;
  text: string;
  disabled?: boolean;
}

/**
 * FloatingCommentButton Component
 * 
 * A floating action button for adding comments, similar to the old app's ChatCommentButton.
 * Positioned at the bottom of the screen with a prominent design.
 * The text prop should be provided by the parent component (GroupDiscussion).
 */
const FloatingCommentButton: React.FC<FloatingCommentButtonProps> = memo(({
  onPress,
  text,
  disabled = false,
}) => {
  const theme = useTheme();
  const tokens = getTokens();

  const bottomOffset = getFloatingBottomOffset(tokens.space.$8.val);

  return (
    <CobuView
      onPress={onPress}
      disabled={disabled}
      bc="$buttonBackground"
      style={{
        position: 'fixed',
        width: '45%',
        bottom: bottomOffset,
        right: '2.5%',
        borderRadius: tokens.radius.$6.val,
        boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
        zIndex: 1000,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <CobuStack layout='horizontal'
        ai="center"
        jc="center"
        gap="$2"
        mih="$8"
        h="auto"
        px="$4"
      >
        <AddCommentIcon
          width={20}
          height={20}
          style={{
            color: theme.buttonText?.val,
          }}
        />
        <CobuText
          variant="body"
          color="$buttonText"
          fos="$2"
        >
          {text}
        </CobuText>
      </CobuStack>
    </CobuView>
  );
});

FloatingCommentButton.displayName = 'FloatingCommentButton';

export default FloatingCommentButton; 